// Third Party
import { useMutation } from "@tanstack/react-query";

// UI
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { Spinner } from "./ui/spinner";

// Icons
import { ImageIcon, FileUp, X } from "lucide-react";
import { handleUploadFile } from "@/services/presign-url";

import { S3_BUCKET_LINK } from "./constants";

interface ImageUploadProps {
	onImageUpload: (url: string) => void;
	currentImage?: string;
	isSubmitting?: boolean;
	showLabel?: boolean;
	label?: string;
	showDeleteButton?: boolean;
}

export function ImageUpload({
	onImageUpload,
	currentImage,
	isSubmitting,
	showLabel = true,
	label = "ອັບໂຫຼດຮູບພາບ",
	showDeleteButton = false,
}: ImageUploadProps) {
	const { toast } = useToast();

	const uploadMutation = useMutation({
		mutationFn: handleUploadFile,
		onSuccess: (filename) => {
			// console.log("Upload successful, filename:", filename);
			onImageUpload(filename);
			toast({
				title: "ສຳເລັດ",
				description: "ອັບໂຫຼດຮູບພາບສໍາເລັດແລ້ວ",
				variant: "success",
			});
		},
		onError: (error) => {
			console.error("Upload failed:", error);
			const fileInput = document.getElementById(
				"imageFile",
			) as HTMLInputElement;
			if (fileInput) fileInput.value = "";
			toast({
				title: "ຫຼົ້ມເຫຼວ",
				description: "ມີບາງຢ່າງຜິດພາດ, ກະລຸນາລອງໃໝ່",
				variant: "destructive",
			});
		},
	});

	return (
		<div className="flex flex-col justify-center mb-4">
			{showLabel ? <p>{label}</p> : null}

			{currentImage ? (
				<div className="min-w-[200px] flex flex-col gap-2 p-2 items-center justify-center relative border rounded-md h-[200px]">
					<img
						src={S3_BUCKET_LINK + currentImage}
						alt="Preview"
						className="max-w-full max-h-[150px] object-contain"
					/>

					<div className="flex gap-2">
						<Button
							type="button"
							variant="outline"
							size="sm"
							className="flex items-center gap-2 text-blue-500 border-blue-500"
							onClick={() => document.getElementById("imageFile")?.click()}
							disabled={isSubmitting || uploadMutation.isPending}
						>
							<FileUp className="w-4 h-4" />
							{uploadMutation.isPending ? (
								<Spinner className="text-blue-500" />
							) : (
								"ປ່ຽນຮູບໃໝ່"
							)}
						</Button>

						{showDeleteButton ? (
							<Button
								size="sm"
								className="text-red-500"
								onClick={() => onImageUpload("")}
							>
								<X className="w-4 h-4 mr-2" />
								ລຶບ
							</Button>
						) : null}
					</div>

					<input
						type="file"
						id="imageFile"
						name="imageFile"
						accept="image/*"
						onChange={(e) => {
							if (e.target.files && e.target.files[0]) {
								uploadMutation.mutate(e);
							}
						}}
						style={{ display: "none" }}
					/>
				</div>
			) : (
				<div className="w-[200px] h-[200px] rounded-md border flex items-center justify-center flex-col">
					<input
						type="file"
						id="imageFile"
						name="imageFile"
						accept="image/*"
						onChange={(e) => {
							if (e.target.files && e.target.files[0]) {
								uploadMutation.mutate(e);
							}
						}}
						style={{ display: "none" }}
					/>
					<Button
						type="button"
						variant="outline"
						className="flex items-center gap-2 text-blue-500 border-blue-500"
						onClick={() => document.getElementById("imageFile")?.click()}
						disabled={isSubmitting || uploadMutation.isPending}
					>
						<ImageIcon />
						{uploadMutation.isPending ? (
							<Spinner className="text-blue-500" />
						) : (
							"ເລືອກຮູບພາບ"
						)}
					</Button>
				</div>
			)}
		</div>
	);
}
